import React, { useState, useEffect } from 'react';
import { Mic, Smile, Zap, Volume2 } from 'lucide-react';
import VoiceSelectionModal from './VoiceSelectionModal';

const PresetIcon = ({ type }) => {
  const iconProps = { size: 20, strokeWidth: 2 };

  const icons = {
    calm: <Smile {...iconProps} />,
    natural: <Mic {...iconProps} />,
    expressive: <Zap {...iconProps} />,
  };

  return icons[type] || null;
};

function VoiceControls({ formData, onFormDataChange, voices, customVoices = [], loading }) {
  const [expressive, setExpressive] = useState(0.8); // 0.6-1.0
  const [showVoiceModal, setShowVoiceModal] = useState(false);

  // Initialize slider from formData on mount
  useEffect(() => {
    setExpressive(formData.temperature || 0.8);
  }, []);

  // Update formData when expressive slider changes
  const handleExpressiveChange = (e) => {
    const newExpressive = parseFloat(e.target.value);
    setExpressive(newExpressive);

    onFormDataChange({
      ...formData,
      temperature: newExpressive,
    });
  };

  // Preset configurations (temperature values)
  const presets = {
    calm: { temperature: 0.7, label: 'Calm', icon: 'calm' },
    natural: { temperature: 0.8, label: 'Natural', icon: 'natural' },
    expressive: { temperature: 0.9, label: 'Expressive', icon: 'expressive' },
  };

  const applyPreset = (presetName) => {
    const preset = presets[presetName];
    setExpressive(preset.temperature);

    onFormDataChange({
      ...formData,
      temperature: preset.temperature,
    });
  };

  // If voice selection modal is open, show it fullscreen in the card
  if (showVoiceModal) {
    return (
      <VoiceSelectionModal
        isOpen={showVoiceModal}
        onClose={() => setShowVoiceModal(false)}
        voices={voices}
        customVoices={customVoices}
        selectedVoice={formData.voice}
        onSelectVoice={(voiceName) => {
          onFormDataChange({
            ...formData,
            voice: voiceName,
          });
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <h3 className="voice-settings-title">
          <Volume2 size={20} strokeWidth={2} style={{ marginRight: '8px', display: 'inline-block' }} />
          Voice Settings
        </h3>
      </div>

      {/* Voice Selection */}
      <div>
        <label className="form-label">Voice</label>
        <button
          type="button"
          className="voice-selection-button"
          onClick={() => setShowVoiceModal(true)}
          disabled={loading}
        >
          <span className="voice-selected-text">
            {formData.voice || 'Select a voice...'}
          </span>
          <span className="voice-button-icon">
            <Mic size={16} strokeWidth={2} />
          </span>
        </button>
      </div>

      {/* Voice Presets */}
      <div>
        <label className="form-label">Presets</label>
        <div className="preset-buttons">
          {Object.entries(presets).map(([key, preset]) => (
            <button
              key={key}
              type="button"
              onClick={() => applyPreset(key)}
              className="preset-button"
              title={`Apply ${preset.label} preset`}
              disabled={loading}
            >
              <span className="preset-icon">
                <PresetIcon type={preset.icon} />
              </span>
              <span className="preset-label">{preset.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Variation Slider */}
      <div>
        <div className="value-display">
          <label htmlFor="expressive" className="form-label">
            Temperature
          </label>
          <span className="value-badge">
            {expressive.toFixed(1)}
          </span>
        </div>
        <input
          id="expressive"
          type="range"
          name="expressive"
          min="0.6"
          max="1"
          step="0.1"
          value={expressive}
          onChange={handleExpressiveChange}
          className="range-slider"
          disabled={loading}
        />
        <p className="helper-text">
          Lower = consistent tone, Higher = varied expression
        </p>
      </div>
    </div>
  );
}

export default VoiceControls;
